import { ScrollReveal, SplitReveal } from '@/components/motion';
import {
  ACCREDITATION_MARKS,
  ACCREDITATIONS,
  INSTITUTIONAL_STATUS,
  RANKINGS,
} from '@/data/about';

/**
 * AccreditationsMemberships (/about — section 4) — "Accredited, ranked &
 * recognised." Presents the five bodies in ACCREDITATIONS as hover-lift cards,
 * each showing the body's real logo on a white plate (lucide Icon kept as an
 * onError fallback), abbr, full name and status/scope; the three QS RANKINGS as
 * large display-figure cards; then the real ACCREDITATION_MARKS logo band using
 * the grayscale-by-default → colour-on-hover idiom from AccreditationLogos, and
 * closes with the INSTITUTIONAL_STATUS disclaimer verbatim.
 *
 * Compliance: EduQua is never mentioned or shown here (it has its own dedicated
 * section/mark). The ACBSP badge is permitted in this card because its scope is
 * IBAS's MBA & DBA accreditation content; it is not used as a general-site
 * badge. Logos sit on white/light fields only, per brand rules.
 *
 * Motion: SplitReveal headline, ScrollReveal child-stagger for the
 * accreditation grid, the rankings row and the logo band. All primitives are
 * reduced-motion safe and never strand content hidden.
 */
export function AccreditationsMemberships() {
  return (
    <section id="accreditations" className="border-t border-border bg-muted/30 py-16 md:py-24">
      <div className="mx-auto w-full max-w-6xl px-6">
        {/* ── Header ─────────────────────────────────────────────────────── */}
        <header className="max-w-2xl">
          <p className="font-label text-xs tracking-[0.22em] text-brand-gold uppercase">
            Recognition
          </p>
          <SplitReveal
            as="h2"
            by="lines"
            className="mt-3 font-display text-3xl leading-tight text-foreground md:text-4xl"
          >
            Accredited, ranked &amp; recognised.
          </SplitReveal>
          <ScrollReveal from="up" delay={0.1}>
            <p className="mt-5 text-base leading-relaxed text-muted-foreground md:text-lg">
              IBAS holds accreditation and membership status with the bodies below, and is
              independently ranked by QS.
            </p>
          </ScrollReveal>
        </header>

        {/* ── Accreditation bodies ──────────────────────────────────────── */}
        <ScrollReveal
          stagger={0.08}
          className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3"
        >
          {ACCREDITATIONS.map((accreditation) => (
            <div
              key={accreditation.abbr}
              className="group flex h-full flex-col rounded-2xl border border-border bg-background p-7 shadow-sm transition-all duration-500 hover:-translate-y-1 hover:border-brand-gold/30 hover:shadow-xl motion-reduce:transition-none motion-reduce:hover:translate-y-0"
            >
              {/* Logo plate — official marks always sit on a white/light field
                  for contrast and brand compliance. The lucide glyph stays as a
                  graceful fallback if the image fails to load. */}
              <div className="flex h-24 w-full items-center justify-center rounded-xl border border-border/60 bg-white p-4">
                <img
                  src={accreditation.logo}
                  alt={accreditation.logoAlt}
                  loading="lazy"
                  className="max-h-16 w-auto max-w-full object-contain transition-transform duration-500 group-hover:scale-[1.03] motion-reduce:transition-none motion-reduce:group-hover:scale-100"
                  onError={(event) => {
                    const img = event.currentTarget;
                    img.style.display = 'none';
                    img.nextElementSibling?.removeAttribute('hidden');
                  }}
                />
                <span hidden>
                  <accreditation.Icon aria-hidden className="size-8 text-brand-navy" />
                </span>
              </div>
              <p className="mt-5 font-display text-lg leading-tight font-semibold text-foreground">
                {accreditation.abbr}
              </p>
              <p className="mt-1 text-sm leading-snug text-muted-foreground">
                {accreditation.name}
              </p>
              <p className="mt-3 text-sm leading-relaxed text-foreground">{accreditation.status}</p>
              {accreditation.detail ? (
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                  {accreditation.detail}
                </p>
              ) : null}
            </div>
          ))}
        </ScrollReveal>

        {/* ── QS rankings ────────────────────────────────────────────────── */}
        <div className="mt-16 border-t border-border pt-12">
          <p className="font-label text-xs tracking-[0.22em] text-brand-gold uppercase">
            QS rankings
          </p>
          <ScrollReveal stagger={0.08} className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-3">
            {RANKINGS.map((ranking) => (
              <div
                key={ranking.label}
                className="h-full rounded-2xl border border-border bg-background p-7 text-center shadow-sm transition-all duration-500 hover:-translate-y-1 hover:border-brand-gold/30 hover:shadow-xl motion-reduce:transition-none motion-reduce:hover:translate-y-0"
              >
                <p className="font-display text-4xl leading-none font-semibold text-brand-navy md:text-5xl">
                  {ranking.figure}
                </p>
                <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
                  {ranking.label}
                </p>
              </div>
            ))}
          </ScrollReveal>
        </div>

        {/* ── Accreditation / ranking marks band ────────────────────────── */}
        <div className="mt-16 border-t border-border pt-12">
          <p className="font-label text-xs tracking-[0.22em] text-brand-gold uppercase">
            Official marks
          </p>
          <ScrollReveal
            stagger={0.08}
            className="mx-auto mt-8 grid grid-cols-2 items-center justify-items-center gap-x-8 gap-y-8 sm:gap-x-10 sm:gap-y-10 lg:grid-cols-5 lg:gap-x-12"
          >
            {ACCREDITATION_MARKS.map((mark) => (
              <div key={mark.src} className="flex h-16 w-full items-center justify-center sm:h-20">
                {/* Official marks — never recoloured, stretched or rotated.
                    Grayscale-by-default resolves to full colour on hover/focus;
                    touch and reduced-motion users see full colour immediately. */}
                <img
                  src={mark.src}
                  alt={mark.alt}
                  loading="lazy"
                  className="max-h-14 w-auto object-contain grayscale-[60%] opacity-70 transition-all duration-300 ease-out hover:grayscale-0 hover:opacity-100 focus-visible:grayscale-0 focus-visible:opacity-100 motion-reduce:grayscale-0 motion-reduce:opacity-100 sm:max-h-16 [@media(hover:none)]:grayscale-0 [@media(hover:none)]:opacity-100"
                />
              </div>
            ))}
          </ScrollReveal>
        </div>

        {/* ── Institutional-status disclaimer ───────────────────────────── */}
        <p className="mt-10 border-t border-border pt-8 text-xs text-muted-foreground">
          {INSTITUTIONAL_STATUS}
        </p>
      </div>
    </section>
  );
}
